import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Filter, Search, X } from 'lucide-react';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    brand: searchParams.get('brand') || '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [catRes, brandsRes, configRes] = await Promise.all([
          api.get('/categories'),
          api.get('/brands'),
          api.get('/config'),
        ]);
        setCategories(catRes.data);
        setBrands(brandsRes.data);
        setConfig(configRes.data);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    })();
    // Registro de visita al catálogo (anónimo, fire-and-forget)
    api.post('/analytics/track-catalog-view').catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (filters.search) {
          const res = await api.post('/products/search', { query: filters.search, limit: 200 });
          setProducts(res.data);
        } else {
          const params = new URLSearchParams();
          if (filters.category) params.append('category', filters.category);
          if (filters.brand) params.append('brand', filters.brand);
          params.append('limit', '500');
          const res = await api.get(`/products?${params.toString()}`);
          setProducts(res.data);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  const updateFilters = (next) => {
    setFilters(next);
    const params = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => v && params.set(k, v));
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => updateFilters({ ...filters, [key]: value });

  const clearFilters = () => {
    setFilters({ search: '', category: '', brand: '' });
    setSearchParams({});
  };

  const activeFiltersCount = (filters.brand ? 1 : 0) + (filters.search ? 1 : 0);

  // Agrupar por categoría cuando NO hay filtro de categoría activo (vista de visión general)
  const groupedByCategory = useMemo(() => {
    if (filters.category || filters.search) return null;
    const map = new Map();
    for (const p of products) {
      const key = p.category || 'sin-categoria';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    // Ordenar categorías por nombre/slug en el orden definido en `categories`
    const orderedSlugs = categories.map((c) => c.slug);
    const sorted = [];
    for (const slug of orderedSlugs) {
      if (map.has(slug)) {
        sorted.push({ slug, name: categories.find((c) => c.slug === slug)?.name || slug, items: map.get(slug) });
        map.delete(slug);
      }
    }
    // Categorías que estuvieran en productos pero no en /categories
    for (const [slug, items] of map.entries()) {
      sorted.push({ slug, name: slug, items });
    }
    return sorted;
  }, [products, categories, filters.category, filters.search]);

  const activeCategoryName = filters.category
    ? categories.find((c) => c.slug === filters.category)?.name || filters.category
    : null;

  return (
    <div className="px-4 md:px-12 lg:px-24 py-12">
      {/* Title block */}
      <div className="mb-8">
        <h1 data-testid="catalog-title" className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl tracking-tighter mb-3">
          {activeCategoryName || (filters.search ? `Resultados: "${filters.search}"` : 'Todos los Productos')}
        </h1>
        <p className="text-xs uppercase tracking-[0.25em] text-white/40">
          {products.length} producto{products.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Category chips — siempre visibles */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="category-chips">
        <button
          onClick={() => handleFilterChange('category', '')}
          data-testid="chip-all"
          className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
            !filters.category
              ? 'bg-white text-black border-white'
              : 'border-white/15 text-white/70 hover:border-white/40 hover:text-white'
          }`}
        >
          Todas
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleFilterChange('category', cat.slug)}
            data-testid={`chip-${cat.slug}`}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
              filters.category === cat.slug
                ? 'bg-white text-black border-white'
                : 'border-white/15 text-white/70 hover:border-white/40 hover:text-white'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Search bar + Filters toggle */}
      <div className="flex flex-col md:flex-row gap-3 mb-10">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Buscar productos, marcas, SKU..."
            data-testid="catalog-search"
            className="w-full bg-[#0A0A0A] border border-white/10 focus:border-white/30 pl-10 pr-3 py-3 text-sm text-white outline-none placeholder-white/30"
          />
          {filters.search && (
            <button
              onClick={() => handleFilterChange('search', '')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            data-testid="filters-toggle-button"
            className="flex items-center gap-2 px-4 py-3 border border-white/10 hover:border-white/40 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filtros
            {activeFiltersCount > 0 && (
              <span className="bg-white text-black text-[10px] px-2 py-0.5 rounded-full">{activeFiltersCount}</span>
            )}
          </button>
          {config?.show_virtual_tryon && (
            <Link
              to="/probador"
              className="hidden sm:flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs uppercase tracking-[0.15em] transition-all"
            >
              Probador
            </Link>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="mb-10 bg-[#0A0A0A] border border-white/10 p-6" data-testid="filters-panel">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold">Filtros avanzados</h3>
            {(activeFiltersCount > 0 || filters.category) && (
              <button onClick={clearFilters} data-testid="clear-filters-button" className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors">
                Limpiar todo
              </button>
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] mb-3 text-white/80">Marca</label>
            <select
              value={filters.brand}
              onChange={(e) => handleFilterChange('brand', e.target.value)}
              data-testid="filter-brand"
              className="w-full md:w-1/2 bg-[#121212] border border-white/10 focus:border-white/30 px-4 py-3 text-white text-sm outline-none"
            >
              <option value="">Todas las marcas</option>
              {brands.map((b) => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="text-sm text-white/60 uppercase tracking-[0.2em]">Cargando productos...</div>
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-32" data-testid="no-products-message">
          <p className="text-lg text-white/60 mb-4">No se encontraron productos</p>
          <button onClick={clearFilters} className="text-xs uppercase tracking-[0.2em] text-white/80 hover:text-white underline">
            Limpiar filtros
          </button>
        </div>
      ) : groupedByCategory ? (
        // Vista agrupada por categoría
        <div className="space-y-16 md:space-y-20" data-testid="grouped-products">
          {groupedByCategory.map((group) => (
            <section key={group.slug} data-testid={`category-section-${group.slug}`}>
              <div className="flex items-end justify-between mb-6 pb-4 border-b border-white/10">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{group.items.length} producto{group.items.length !== 1 ? 's' : ''}</p>
                  <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl tracking-tighter">{group.name}</h2>
                </div>
                <button
                  onClick={() => handleFilterChange('category', group.slug)}
                  className="text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors"
                >
                  Ver todo →
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {group.items.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    navigationContext={{ category: group.slug, categoryName: group.name }}
                    showPrices={config?.show_prices !== false}
                    lowStockThreshold={config?.low_stock_threshold ?? 30}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        // Vista filtrada (una sola grilla)
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-testid="products-grid">
          {products.map((product) => {
            const navContext = filters.category
              ? { category: filters.category, categoryName: activeCategoryName }
              : null;
            return (
              <ProductCard
                key={product.id}
                product={product}
                navigationContext={navContext}
                showPrices={config?.show_prices !== false}
                lowStockThreshold={config?.low_stock_threshold ?? 30}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Catalog;
